import { useEffect, useRef, useState } from "react";
import { getAudioGraphInstance } from '../audioEngine';
import { useSelector } from "react-redux";
import { getScaleRootAndType } from "$utils/music"; // music theory related
import isequal from 'lodash.isequal';
import { midiHandler } from "../MidiHandler";
import playhead_img from "url:$assets/widgets/playhead_long.svg";
import { selectTransportCurrentStep, selectTonality, selectIsPlaying } from "../store";
import { createSelector } from "@reduxjs/toolkit";
import { getPCNumber } from "../utils/music";
import { RadioGroup } from "../components/RadioGroup";
import { RadioButton } from "../components/RadioButton";


export const PianoRoll = ({ className, instrument, colorNotes, modeSampler, triggerNotes=true }) => {
    const sequence = useSelector(instrument.getDisplaySequenceSelector());
    const numSteps =  useSelector(instrument.getDisplayClipLengthSelector());
    const currentStep = useSelector(createSelector(selectTransportCurrentStep, currentStep => currentStep % numSteps));
    const uniqueId = instrument.id + "_piano-roll";
    let lastEditedData = "";
    const colorCodeDescription = instrument.getParameterDescription("pianoRollColorCode");
    const colorCode = colorCodeDescription ? useSelector(instrument.getDisplayParameterSelector("pianoRollColorCode")) : "none";
    
    const tonality = useSelector(selectTonality);
    const {root, type: mode} = getScaleRootAndType(tonality);
    const rootPC = getPCNumber(root);

    const instrumentRange = instrument.pitchRange.usable.max - instrument.pitchRange.usable.min + 1;

    const wrapperRef = useRef();
    function applyDimensions() {
        const jsElement = document.getElementById(uniqueId);
        const { width, height } = wrapperRef.current.getBoundingClientRect();
        jsElement.setDimensions({width, height});
    }
    useObserve(wrapperRef, applyDimensions);

    useEffect(() => {
        const jsElement = document.getElementById(uniqueId);
        jsElement.setRootPC(rootPC);
        jsElement.setScaleType(mode);
    }, [tonality]);
    useEffect(() => {
        const jsElement = document.getElementById(uniqueId);
        jsElement.setColorCodeMode(colorCode);
    }, [colorCode]);
    useEffect(() => {
        const jsElement = document.getElementById(uniqueId);
        jsElement.setXRange(numSteps);
    }, [numSteps]);

    const isPlaying = useSelector(selectIsPlaying);

    useEffect(() => {
        const jsElement = document.getElementById(uniqueId);
        const controller = new AbortController();
        jsElement.addEventListener("pianoRollEdited", evt => {
            const stringifiedData = JSON.stringify(evt.detail)
            if (!isequal(stringifiedData, lastEditedData)) {
                handleSequenceEdited(evt.detail)
                lastEditedData = stringifiedData // Save using stringified version to avoid using a reference. If using a reference, "isequal" above will always be true after the first iteration
            }
        }, { signal: controller.signal });
        if (triggerNotes){
            jsElement.addEventListener("pianoRollNoteSelectedOrCreated", evt => {
                // When a note is created or selected, we will trigger a midi note
                if (isPlaying) return; // but not if it's playing
                midiHandler.sendNoteOn(instrument.id, evt.detail.pitch, evt.detail.velocity);
                setTimeout(() => {
                    midiHandler.sendNoteOff(instrument.id, evt.detail.pitch, 0);
                }, evt.detail.durationInBeats * getAudioGraphInstance().get16BeatTime() * 1000);
            }, {signal: controller.signal});
            jsElement.addEventListener("pianoRollKeyboardNote", evt => {
                // When a note is played in the display keyboard, we will trigger a midi note
                if (evt.detail.noteOff == true) {
                    midiHandler.sendNoteOff(instrument.id, evt.detail.pitch, 0);
                } else {
                    midiHandler.sendNoteOn(instrument.id, evt.detail.pitch, evt.detail.velocity);
                }
            }, {signal: controller.signal});
        }

        return () => {
            controller.abort();
        }
    }, [isPlaying])

    useEffect(() => {
        const jsElement = document.getElementById(uniqueId);
        const newWidgetSequence = appSequenceToWidgetSequence(sequence);
        const oldWidgetSequence = jsElement.sequence;
        if (!isequal(oldWidgetSequence, newWidgetSequence)) {
            jsElement.sequence = newWidgetSequence.map(notaNova => {
                // si la nota ja existia, la coloquem amb la mateixa referència amb Object.assign
                // així, el widget sap que és la mateixa nota, i mantindrà el seu estat de selecció i s'hi podrà interactuar bé
                const notaAntigua = oldWidgetSequence.find(nota => nota.id === notaNova.id);
                if (notaAntigua) return Object.assign(notaAntigua, notaNova);
                return {...notaNova, f: 0};
            });
            jsElement.redraw()
        }
        if (currentStep >= 0 && staticSelect(instrument.getDisplayParameterSelector("clipNumber")) === instrument.getPlaybackParameterValue("clipNumber")) {
            jsElement.locate(currentStep);
        } else {
            jsElement.locate(-10);  // make it dissapear
        }
    })

    const appSequenceToWidgetSequence = (sequence) => {
        return sequence.map(value => {return {
            t: value.onset,  // time in beats
            n: value.pitch,  // midi pitch number
            g: value.duration,  // note duration in beats
            v: value.velocity,
            id: value.id, // identifier
        }})
    }

    const widgetSequenceToAppSequence = (wSequence) => {
        return wSequence.map(value => {return {
            onset: value.t,  // beat position
            pitch: value.n,  // midi pitch number
            duration: value.g,  // note duration in beats
            velocity: value.v,
            id: value.id, // identifier
        }})
    }

    const handleSequenceEdited = (widgetSequence) => {
        instrument.updateSequence(widgetSequenceToAppSequence(widgetSequence));
    }


    const maxYRange = 36;
    const doesYScroll = instrumentRange > maxYRange;

    const getLowestNoteForYOffset = () => {
        // if the roll doesn't scroll, simply return the lowest roll
        if (!doesYScroll) return instrument.pitchRange.usable.min;

        // else, return the lowest drawn note, if there are any notes drawn on the roll
        // if (sequence && sequence.map(note => note.pitch)) return sequence.map(note => note.pitch).reduce((min, value) => Math.min(min, value));

        // else, return a sensible default, if it exists
        if (instrument.pitchRange.typical) return instrument.pitchRange.typical.min;

        return 0;
    }

    // Available webaudio-pianoroll attributes: https://github.com/g200kg/webaudio-pianoroll

    return <div data-widget-id="pianoRoll" ref={wrapperRef} className={`gruf-piano-roll ${className}`} style={{ overflow:"hidden"}}>
            <gruf-pianoroll
                id={uniqueId}
                editmode={instrument.isMono ? "dragmono" : "dragpoly"}
                instrumentid={instrument.id}
                rootpc={rootPC}
                scaletype={mode}
                colorcodemode={colorCode}
                grid={2}
                xrange={numSteps}
                yrange={Math.min(instrumentRange, maxYRange)}
                yoffset={modeSampler === undefined ? getLowestNoteForYOffset(): 0}
                xruler={20}
                markstart={-10}  // make it dissapear
                markend={-10}  // make it dissapear
                //cursoroffset={2500}  // make it dissapear
                yscroll={doesYScroll ? 1 : 0} // only allow scroll when there is 'overflow'
                //xscroll={true}
                accentColor={colorNotes || "#f22"}
                colnotedissalowed="#333"    
                collt={"rgb(200, 200, 200)"}
                coldk={"rgb(176, 176, 176)"}
                colgrid={"#999"}
                colrulerbg={"#4b4b4b"}
                colrulerfg={"#fff"}
                colrulerborder={"#4b4b4b"}
                cursorsrc={playhead_img}
                kbwidth={modeSampler === undefined ? 65: 0}
                kbstyle={modeSampler === undefined ? "piano": "midi"}
                yruler={modeSampler === undefined ? 20: 22}
            ></gruf-pianoroll>
    </div>
};

import { Ellipsis } from 'lucide-react';
import { GripVertical } from 'lucide-react';
import { CircleOff } from 'lucide-react';
import { staticSelect } from "../utils/react";
import { useObserve } from "../hooks/useObserve";

export const ColorCodeSelector = ({instrument}) => {
    const colorCodeDescription = instrument.getParameterDescription("pianoRollColorCode");
    const colorCode = useSelector(instrument.getDisplayParameterSelector("pianoRollColorCode"));

    const modes = {
        "melodic": {
            icon: Ellipsis,
            widgetId: "pianoRollDiatonicColoring"
        },
        "harmonic": {
            icon: GripVertical,
        },
        "none": {
            icon: CircleOff,
            widgetId: "pianoRollChromaticColoring"
        }
    }
    return <div className="buttongroup" style={{display: "flex", "--border-radius": "4px"}}>
        <RadioGroup>
            {colorCodeDescription.options.map(option => {
                const Icon = modes[option].icon
                return (
                    <RadioButton
                        data-widget-id={modes[option].widgetId}
                        className="btn w-30"
                        style={{display: "grid", placeContent: "center"}}
                        key={option} 
                        checked={colorCode == option} 
                        onNewValue={(value) => value && instrument.updateParametreInstrument("pianoRollColorCode", option)}
                    >
                        <Icon />
                    </RadioButton>
                )
            })}
        </RadioGroup>
    </div>
}